import { AuthLogger } from './logger';

/**
 * SimpleStorageService
 *
 * A straightforward storage management class that provides direct access
 * to sessionStorage and localStorage without JSON serialization,
 * auto-deletion, or complex structures.
 *
 * Features:
 * - Direct string storage/retrieval
 * - Session and local storage support
 * - Simple error handling
 * - No namespacing or complex transformations
 */
export class SimpleStorageService {
  private useSessionStorage: boolean;

  constructor(options: { useSessionStorage: boolean }) {
    this.useSessionStorage = options.useSessionStorage;
  }

  private getStorage(): Storage {
    return this.useSessionStorage ? sessionStorage : localStorage;
  }

  /**
   * Store a value as JSON (consistent with Core module)
   */
  save(key: string, value: string | object): void {
    try {
      this.getStorage().setItem(key, JSON.stringify(value));
    } catch (error) {
      AuthLogger.warn(
        `Failed to save to ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        key,
        error
      );
    }
  }

  /**
   * Retrieve a value as a string (or null if not found)
   * Handles both raw strings and JSON-stringified values from Core module
   */
  retrieve(key: string): string | null {
    try {
      const rawValue = this.getStorage().getItem(key);
      if (rawValue === null) {
        return null;
      }

      // Try to parse as JSON first (for Core module compatibility)
      // If it fails, return the raw value (for direct string storage)
      try {
        const parsed = JSON.parse(rawValue);
        // If parsed value is a string, return it; otherwise return raw value
        return typeof parsed === 'string' ? parsed : rawValue;
      } catch {
        // Not valid JSON, return raw value
        return rawValue;
      }
    } catch (error) {
      AuthLogger.warn(
        `Failed to retrieve from ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        key,
        error
      );
      return null;
    }
  }

  /**
   * Remove a value
   */
  remove(key: string): void {
    try {
      this.getStorage().removeItem(key);
    } catch (error) {
      AuthLogger.warn(
        `Failed to remove from ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        key,
        error
      );
    }
  }

  /**
   * Check if a key exists
   */
  exists(key: string): boolean {
    try {
      return this.getStorage().getItem(key) !== null;
    } catch (error) {
      AuthLogger.warn(
        `Failed to check existence in ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        key,
        error
      );
      return false;
    }
  }

  /**
   * Clear all items from storage
   */
  clear(): void {
    try {
      this.getStorage().clear();
    } catch (error) {
      AuthLogger.warn(
        `Failed to clear ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        error
      );
    }
  }

  /**
   * Get all keys in storage
   */
  getAllKeys(): string[] {
    try {
      const storage = this.getStorage();
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      AuthLogger.warn(
        `Failed to get all keys from ${this.useSessionStorage ? 'session' : 'local'} storage:`,
        error
      );
      return [];
    }
  }
}
